import { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import useSortableTable from "../../hooks/useSortableTable";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SearchBar from "../UI/SearchBar";
import EditCompetitionDialog from "./EditCompetitionDialog";
import DeleteCompetitionDialog from "./DeleteCompetitionDialog";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import { LoginContext } from "../../Components/Login/LoginContext";

const CompetitionsTable = (props) => {
  const navigate = useNavigate();
  const [auth] = useContext(LoginContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");

  const rows = props.competitions.map(({ id, name, description, url, user }) => ({
    id,
    name,
    description,
    url,
    owner: user.username,
  }));

  const competitionsWithPermission = props.competitionsWithPermission.map(
    (c) => c.id
  );

  const { sorted: sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows, "name");

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<EmojiEventsRoundedIcon />}
        message="No competitions yet."
      />
    );
  }

  const q = search.toLowerCase();
  const sorted = sortedRows.filter(
    (row) =>
      row.name.toLowerCase().includes(q) ||
      (row.description || "").toLowerCase().includes(q) ||
      row.owner.toLowerCase().includes(q)
  );

  const noResults = sorted.length === 0;

  return (
    <>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search competitions..."
      />

      {noResults ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No competitions match &ldquo;{search}&rdquo;.
        </Typography>
      ) : isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sorted.map((row) => (
            <Card
              key={row.id}
              data-testid={"competitionTableRow" + row.id}
              sx={{ cursor: "pointer", backgroundColor: "#2a3447" }}
              onClick={() => navigate(`/competition/${row.id}`)}
            >
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {row.name}
                    </Typography>
                    {row.description && (
                      <Typography variant="body2" color="text.secondary">
                        {row.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Owner: {row.owner}
                    </Typography>
                  </Box>
                  {competitionsWithPermission.includes(row.id) && (
                    <RowActionsMenu
                      onEdit={() => setEditRow(row)}
                      onDelete={() => setDeleteRow(row)}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer
          className="tableContainer"
          data-testid="competitionsTable"
          component={Paper}
          sx={{ overflowX: "auto" }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {competitionsWithPermission.length > 0 && <TableCell sx={{ width: 48, p: 0.5 }} />}
                <TableCell sortDirection={sortKey === "name" ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === "name"}
                    direction={sortKey === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Description</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow
                  data-testid={"competitionTableRow" + row.id}
                  hover
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                  onClick={() => navigate(`/competition/${row.id}`)}
                >
                  {competitionsWithPermission.length > 0 && (
                    <TableCell sx={{ width: 48, p: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {competitionsWithPermission.includes(row.id) && (
                        <RowActionsMenu
                          onEdit={() => setEditRow(row)}
                          onDelete={() => setDeleteRow(row)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.url}</TableCell>
                  <TableCell>{row.owner}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editRow && (
        <EditCompetitionDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          competition={editRow}
        />
      )}
      {deleteRow && (
        <DeleteCompetitionDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          competition={deleteRow}
        />
      )}
    </>
  );
};

export default CompetitionsTable;
